// admin-analytics-expansion P1 — Org-wide KPI rollup cards.
//
// Purely presentational: renders `OrgAnalyticsResponse.total` (an
// `AnalyticsKpi`) using the same StatCard + formatCents/formatAvgSeconds
// convention as the per-location cards in AnalyticsPage. `total` is
// `undefined` while the parent query is loading — each card falls back to
// its StatCard skeleton, mirroring the existing Location-tab loading pattern.
//
// NOTE: currency stays hardcoded USD (see AnalyticsPage's CURRENCY_FALLBACK
// comment) — P1 explicitly does not extend the `ticket-analytics` contract
// with a currency field.

import { useTranslation } from '@jq4race/i18n';
import { type AnalyticsKpi, DEFAULT_CURRENCY } from '@jq4race/shared';
import { formatAvgSeconds, formatCents, StatCard } from '@jq4race/ui';
import { Car, KeyRound, Users as UsersIcon } from 'lucide-react';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;

export interface OrgRollupProps {
  total: AnalyticsKpi | undefined;
}

export function OrgRollup({ total }: OrgRollupProps) {
  const { t } = useTranslation('analytics');
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
        icon={UsersIcon}
        label={t('kpi.totalVolume')}
        value={total ? total.volume.toString() : null}
      />
      <StatCard
        icon={KeyRound}
        label={t('kpi.totalRevenue')}
        value={total ? formatCents(total.netRevenueCents, CURRENCY_FALLBACK) : null}
      />
      <StatCard
        icon={Car}
        label={t('kpi.avgRetrievalTime')}
        value={
          total
            ? total.avgRetrievalSeconds !== null
              ? formatAvgSeconds(total.avgRetrievalSeconds)
              : '—'
            : null
        }
      />
    </div>
  );
}
